import { CSSProperties } from 'react';
import { IBanner, IFeature, IFeatureBanner, IFeatureBlog } from 'mod/feature/interface';
import { ITrackElementData } from 'base/utils/tracker';
import { IProduct, ITrackVariant } from 'base/interfaces/product';
import { ITopPromotionBar } from 'base/interfacesV2/feature';
import { ModuleTitleProps } from '@base/lib/ModuleTitle';
import { get750RemValue } from 'base/utils/dom-size';

type IGetFeatureProductTrackDataParams = {
    moduleCode: string;
    trackCode?: string;
    item: IProduct;
    index: number;
    targetPrefix?: string;
};

// 获取打点module code
export function getModuleCode(data: { moduleId: string; }): string {
    return data.moduleId ? `${process.env.NEXT_PUBLIC_TERMINAL === 'm' ? 'H5' : 'WEB'} ${data.moduleId}` : '';
}

export function getModuleElementId(data: IFeature): string {
    return data.moduleId ? data.moduleId.trim().replaceAll(' ', '-').toLowerCase() : ''
}

export function getFeatureBannerTrackData(data: IFeatureBanner, index: number): Omit<ITrackElementData, 'action'> {
    const item = data.lists[index];
    return {
        targetType: 'banner',
        target: item.linkUrl || item.videoUrl || item.picUrl || '',
        relTarget: item.linkUrl || item.videoUrl || item.picUrl,
        module: getModuleCode(data),
        position: index + 1,
        creative: item.videoUrl || item.picUrl,
    }
}

export function getBannerTrackData(banner: IBanner, moduleId: string, index: number): Omit<ITrackElementData, 'action'> {
    return {
        targetType: 'banner',
        target: banner.linkUrl || banner.videoUrl || banner.picUrl || '',
        relTarget: banner.linkUrl || banner.videoUrl || banner.picUrl,
        module: moduleId,
        position: index + 1,
        creative: banner.videoUrl || banner.picUrl,
    }
}

export function getFeatureProductTrackData(parmas: IGetFeatureProductTrackDataParams): (ITrackElementData & ITrackVariant) {
    const {
        moduleCode,
        trackCode,
        item,
        index,
        targetPrefix = ''
    } = parmas;

    const targetTrackCode = item.variantTrackCode || item.productTrackCode;
    const target = targetPrefix ? `${targetPrefix}:${targetTrackCode}` : targetTrackCode;

    return {
        ...item,
        listTrackCode: trackCode || moduleCode,
        module: moduleCode,
        targetType: item.variantId ? 'variant' : 'product',
        target,
        relTarget: target,
        position: index + 1,
    }
}

export function getFeatureMoreTrackData(moduleCode: string): Omit<ITrackElementData, 'action'> {
    return {
        targetType: 'other',
        target: 'view-more',
        relTarget: 'view-more',
        module: moduleCode,
        position: 'view-more',
    }
}

export function getTopPromotionBarStyles(config: ITopPromotionBar): {
    bgStyle: CSSProperties;
    textStyle: CSSProperties;
} {
    const { back, textColor } = config;
    const bgStyle: CSSProperties = {};
    const textStyle: CSSProperties = {};
    if (back?.pic) {
        bgStyle.backgroundImage = `url('${back.pic}')`;
        bgStyle.backgroundPosition = 'top center';
    }
    if (back?.color) {
        bgStyle.backgroundColor = back.color;
    }

    if (textColor) {
        textStyle.color = textColor;
    }
    return {
        bgStyle,
        textStyle
    }
}

export function getFeatureTitleProps(data: IFeature, title: undefined | false | ModuleTitleProps): ModuleTitleProps | null {
    if (title === false) {
        return null;
    }

    return {
        text: data.title,
        link: data.readMoreLink,
        linkText: data.readMoreTitle,
        endTimeStamp: data.endTimeStamp,
        moduleCode: data.moduleId ? getModuleCode(data) : '',
        ...title
    }
}

export function getFeatureBlogTrackData(moduleCode: string, data: IFeatureBlog, index: number): Omit<ITrackElementData, 'action'> {
    const item = data.lists[index];
    return {
        targetType: 'list',
        target: item.linkUrl,
        relTarget: item.linkUrl,
        module: getModuleCode(data),
        position: index + 1,
        creative: item.picUrl,
    }
}

export function getWrapSpaceStyle(space: IFeature['space']): CSSProperties {
    const style: CSSProperties = {};
    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';
    if (space) {
        Object.entries(space).forEach(([key, value]) => {
            if (value > 0) {
                style[key] = isPhone ? get750RemValue(value) : `${value}px`;
            }
        });
    }
    return style;
}

export function getWrapBgStyle(background): CSSProperties {
    const style: CSSProperties = {};
    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';
    if (background?.image) {
        style.backgroundImage = `url('${background.image}')`;
        style.backgroundPosition = 'top center';
        style.backgroundSize = 'cover';
    }
    if (background?.color) {
        style.background = background.color;
    }

    return style;
}

export function getWrapStyle({ background, space }: IFeature): CSSProperties {

    return {
        ...getWrapBgStyle(background),
        ...getWrapSpaceStyle(space)
    };
}